import React, { useEffect, useRef, useState } from "react";
import { Loader2, Phone, Mail, ChevronDown, Send } from "lucide-react";
import { GetStaffId } from "../../adapters/utils";
import { Chip } from "@mui/material";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useParams } from "react-router-dom";
import {
  useGetAllinformationByid,
  useGetDepartmentsByBusinessUnit,
  usePostMailApplicationById,
} from "../../adapters/Requests";
import ApplicantPhoto from "./ApplicantPhoto";
import { applicationStatuses, internshipTypes } from "../../utils/constants";

const CustomSelect = ({ options, value, onChange, title, zIndex = 10 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={dropdownRef}
      className="relative inline-block text-left w-full mt-1 mb-2"
    >
      <button
        type="button"
        className="inline-flex justify-between w-full rounded-md border border-gray-300 shadow-sm px-4 py-2.5 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {value
          ? options.find((opt) => opt.value === value)?.label
          : "Select " + title}
        <ChevronDown className="-mr-1 ml-2 h-4 w-4 text-gray-400" />
      </button>

      {isOpen && (
        <div className="origin-top-right absolute right-0 mt-2 w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="py-1 max-h-[35vh] overflow-auto">
            {options.map((option) => (
              <a
                key={option.value}
                href="#"
                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={(e) => {
                  e.preventDefault();
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                {option.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

function FinalAssessmentMail({
  isSiwes,
  siwesCourseQuota,
  siwesCoursedUsedSlots,
  applicationId,
  onActionComplete,
}) {
  const [department, setDepartment] = useState("");
  const [childBusinessUnitId, setChildBusinessUnitId] = useState(null);
  const [openAcceptDialog, setOpenAcceptDialog] = useState(false);
  const [openDeclineDialog, setOpenDeclineDialog] = useState(false);
  const [openAcceptConfirmationDialog, setOpenAcceptConfirmationDialog] =
    useState(false);
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [comment, setComment] = useState("");
  const [departments, setDepartments] = useState(null);

  const params = useParams();
  const application_id = applicationId || params.application_id;

  const { data, isLoading, isError, refetch } = useGetAllinformationByid({
    application_id,
  });
  const { data: departmentResponse, isLoading: isDepartmentsLoading } = useGetDepartmentsByBusinessUnit(
    { business_unit_id: childBusinessUnitId },
    { enabled: !!childBusinessUnitId }
  );

  const businessUnit = data?.vacancy?.business_unit;
  const vacancyBUDepartments = businessUnit?.departments;
  const childBusinessUnits = businessUnit?.children;

  useEffect(() => {
    if (vacancyBUDepartments?.length > 0) setDepartments(vacancyBUDepartments);
  }, [vacancyBUDepartments]);

  useEffect(() => {
    if (departmentResponse?.departments)
      setDepartments(departmentResponse.departments);
  }, [departmentResponse]);

  const AcceptanceMail = usePostMailApplicationById();
  const { APPROVED, ACCEPTED, DECLINED } = applicationStatuses;

  if (isError) return <div>Error reload or go back</div>;

  const displayStatus = data?.status === "COMPLETED" ? ACCEPTED : data?.status;

  const handleMarkStatus = async (status_new, dept = "") => {
    setIsConfirming(true);
    const selectedChildBu = childBusinessUnits?.find(
      (bu) => bu.id === childBusinessUnitId,
    );
    const business_unit_code =
      selectedChildBu?.code ?? businessUnit?.code;
    const formData = {
      application_id,
      status: status_new,
      department: dept,
      ...(business_unit_code ? { business_unit_code } : {}),
    };
    if (status_new === DECLINED) formData.comment = comment;

    try {
      if (status_new === ACCEPTED && !dept)
        throw new Error("Please select a department");
      const response = await AcceptanceMail.mutateAsync({ formData });

      refetch();
      handleCloseAcceptDialog();
      handleCommentDialogClosure();
      setOpenAcceptConfirmationDialog(false);
      setOpenDeclineDialog(false);
      toast.success(response.message);
      onActionComplete?.();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong!");
    } finally {
      setIsConfirming(false);
    }
  };


  const handleOpenAcceptDialog = () => setOpenAcceptDialog(true);
  const handleCloseAcceptDialog = () => setOpenAcceptDialog(false);
  const handleCommentDialogClosure = () => {
    setOpenCommentDialog(false);
    setComment("");
  };
  const handleCommentSubmit = () => {
    if (comment.length < 3) return;
    setOpenDeclineDialog(true);
  };

  const departmentOptions = departments?.map((dept) => ({
    value: dept.code,
    label: dept.name,
  }));
  const childBusinessUnitOptions = childBusinessUnits?.map((bu) => ({
    value: bu.id,
    label: bu.name,
  }));

  const applicantSelectedCourse =
    data?.ApplicationsAcademicQualification?.course;
  const selectedVacancyCourse = data?.vacancy?.vacancyCourses?.find((c) =>
    c && applicantSelectedCourse
      ? c.id == applicantSelectedCourse.id ||
      c.courseId == applicantSelectedCourse.id
      : false
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden w-full mb-6">
      <div className="bg-slate-50 px-6 py-3 border-b border-slate-100 flex items-center gap-2">
        <Send size={16} className="text-slate-500" />
        <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
          Accept & Deploy
        </h2>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
          </div>
        ) : data ? (
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <ApplicantPhoto applicationId={application_id} />

            <div className="flex flex-col items-end gap-4 w-full md:w-auto">
              <div className="flex items-center gap-2">
                {(!isSiwes || siwesCourseQuota > siwesCoursedUsedSlots) &&
                  displayStatus === APPROVED && (
                    <div className="flex gap-3">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white h-9"
                        onClick={handleOpenAcceptDialog}
                      >
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50 h-9"
                        onClick={() => setOpenCommentDialog(true)}
                      >
                        Decline
                      </Button>
                    </div>
                  )}
                {isSiwes && siwesCourseQuota <= siwesCoursedUsedSlots && (
                  <span className="text-xs font-bold text-red-600 bg-red-50 px-3 py-1.5 rounded border border-red-200">
                    Quota Filled
                  </span>
                )}
                {displayStatus === ACCEPTED && (
                  <Chip
                    label={ACCEPTED}
                    color="success"
                    variant="filled"
                    size="small"
                  />
                )}
                {displayStatus === DECLINED && (
                  <Chip
                    label={DECLINED}
                    color="error"
                    variant="filled"
                    size="small"
                  />
                )}
              </div>

              <div className="flex flex-wrap justify-end gap-x-6 gap-y-2 text-xs text-slate-500">
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-medium">
                    {data?.Applicant?.phone_number}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  <span className="font-medium">{data?.Applicant?.email}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 text-center text-gray-500 italic">
            No data available
          </div>
        )}
      </div>

      {/* Dialogs */}
      <Dialog open={openAcceptDialog} onOpenChange={handleCloseAcceptDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Accept And Deploy</DialogTitle>
            <DialogDescription>
              Deploy to{" "}
              <strong>
                {businessUnit?.name} {data?.vacancy?.location?.name}
              </strong>
              .
              {isSiwes && siwesCourseQuota > siwesCoursedUsedSlots && (
                <div className="mt-3 p-3 bg-green-50 border border-green-100 rounded text-xs">
                  <span className="block font-semibold">
                    Course: {applicantSelectedCourse?.name}
                  </span>
                  <span className="block text-green-700 mt-1">
                    Remaining Quota: {siwesCourseQuota - siwesCoursedUsedSlots}
                  </span>
                </div>
              )}
            </DialogDescription>
            {childBusinessUnitOptions?.length > 0 && (
              <div className="">
                <label className="text-xs mt-4 font-bold text-slate-500 uppercase tracking-wide block">
                  Business Unit
                </label>
                <CustomSelect
                  options={childBusinessUnitOptions}
                  value={childBusinessUnitId}
                  onChange={setChildBusinessUnitId}
                  title="Business Unit"
                  zIndex={50}
                />
              </div>
            )}
            {(childBusinessUnitId || businessUnit?.parent_code) &&
              isDepartmentsLoading ? (
              <div className="mt-4">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">
                  Department
                </label>
                <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                  Loading departments...
                </div>
              </div>
            ) : (childBusinessUnitId || businessUnit?.parent_code) &&
            departmentOptions && (
              <div className="mt-4">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1 block">
                  Department
                </label>
                <CustomSelect
                  options={departmentOptions}
                  value={department}
                  onChange={setDepartment}
                  title="Department"
                  zIndex={40}
                />
              </div>
            )}
          </DialogHeader>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={handleCloseAcceptDialog}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => {
                if (!department) return;
                setOpenAcceptConfirmationDialog(true);
              }}
              disabled={isConfirming || isDepartmentsLoading || !department}
            >
              {isDepartmentsLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
              ) : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={openAcceptConfirmationDialog}
        onOpenChange={setOpenAcceptConfirmationDialog}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Acceptance</DialogTitle>
            <DialogDescription>
              Are you sure you want to accept and deploy this applicant?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setOpenAcceptConfirmationDialog(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => handleMarkStatus("ACCEPTED", department)}
              disabled={isConfirming}
            >
              {isConfirming && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}{" "}
              {isConfirming ? "Accepting..." : "Accept"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openCommentDialog} onOpenChange={setOpenCommentDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Comment</DialogTitle>
            <DialogDescription>
              Provide a reason for declining.
            </DialogDescription>
          </DialogHeader>
          <textarea
            rows={4}
            className="w-full resize-none border border-slate-200 rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter comment..."
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCommentDialogClosure}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleCommentSubmit}
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openDeclineDialog} onOpenChange={setOpenDeclineDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Confirm Decision</DialogTitle>
            <DialogDescription>
              This action will end the process. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setOpenDeclineDialog(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 text-white"
              onClick={() => handleMarkStatus(DECLINED)}
            >
              {isConfirming && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}{" "}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </div>
  );
}

export default FinalAssessmentMail;
